import React, { useState } from 'react';
import styled from 'styled-components';
import { X, User } from 'lucide-react';
import { validateUserName } from '../../utils/userUtils';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  
  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
  }
  
  .close-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    color: #6b7280;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f3f4f6;
      color: #374151;
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #2c3e50;
    font-size: 14px;
  }
  
  input {
    width: 100%;
    padding: 12px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    font-size: 14px;
    transition: border-color 0.2s ease;
    
    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
    
    &.error {
      border-color: #dc3545;
    }
  }
  
  .error-message {
    color: #dc3545;
    font-size: 12px;
    margin-top: 4px;
  }
  
  .help-text {
    color: #6c757d;
    font-size: 12px;
    margin-top: 4px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    }
    
    &:active:not(:disabled) {
      transform: translateY(0);
    }
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  }
`;

const UserSettingsModal = ({ isOpen, onClose, user, onSave }) => {
  const [nickname, setNickname] = useState(user?.name || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    // 验证用户名称
    const validation = validateUserName(nickname);
    if (!validation.isValid) {
      setError(validation.message);
      return;
    }

    setIsLoading(true);
    try {
      await onSave({ name: nickname });
      onClose();
    } catch (error) {
      setError('保存失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setNickname(user?.name || '');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h3>用户设置</h3>
          <button className="close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </ModalHeader>
        
        <FormGroup>
          <label>
            <User size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            昵称
          </label>
          <input
            type="text"
            value={nickname}
            onChange={(e) => {
              setNickname(e.target.value);
              setError('');
            }}
            placeholder="请输入您的昵称"
            className={error ? 'error' : ''}
            maxLength={20}
          />
          {error && <div className="error-message">{error}</div>}
          <div className="help-text">
            昵称将显示在应用中，建议使用2-20个字符
          </div>
        </FormGroup>
        
        <ButtonGroup>
          <Button className="secondary" onClick={handleClose} disabled={isLoading}>
            取消
          </Button>
          <Button 
            className="primary" 
            onClick={handleSave}
            disabled={isLoading || !nickname.trim()}
          >
            {isLoading ? '保存中...' : '保存'}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};

export default UserSettingsModal; 